/**
 * iter_206 · iter_212 · iter_213 — Daily Tasks card.
 *
 * • Fetches /api/daily-tasks/me — server verifies every completion.
 * • iter_212 — Collapsible with localStorage persistence.
 * • iter_213 — 7-day Weekly Rank-Up: task targets scale by day (e.g.
 *   gift 3 → 5 → 7 → 10 → 12 → 15 → 20). Header shows 🔥 Day X of 7 and
 *   the current streak. On Day 7 a weekly-completion bonus card
 *   appears; card unlocks the moment all Day 7 tasks are done.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const COLLAPSE_KEY = "rgm.daily_tasks.collapsed";

const readCollapsed = () => {
  try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
};
const writeCollapsed = (v) => {
  try { localStorage.setItem(COLLAPSE_KEY, v ? "1" : "0"); } catch (_e) { /* storage full */ }
};

const DailyTasksCard = ({ className = "" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(readCollapsed());
  const [autoCollapsed, setAutoCollapsed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/daily-tasks/me");
        if (!cancelled) setData(r.data || null);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const summary = useMemo(() => {
    const tasks = data?.tasks || [];
    // iter_222 — Use server-provided core/bonus split when available;
    // fall back to filtering on category for back-compat.
    const coreTasks = data?.core_tasks || tasks.filter((t) => (t.category || "core") === "core");
    const bonusTask = data?.bonus_task || tasks.find((t) => t.category === "bonus") || null;
    const coreCompleted = coreTasks.filter((t) => t.completed).length;
    const coreTotal = coreTasks.length;
    const bonusCompleted = bonusTask ? !!bonusTask.completed : false;
    // Streak = CORE only. Bonus never gates the streak.
    const completed = coreCompleted;
    const total = coreTotal;
    const cap = data?.daily_cap || 200;
    const earned = data?.earned_today || 0;
    const capReached = earned >= cap;
    const streak = data?.streak || {};
    return {
      tasks, coreTasks, bonusTask, coreCompleted, coreTotal,
      bonusCompleted, completed, total, cap, earned, capReached,
      day: streak.current_day || 1,
      streakDays: streak.streak_days || 0,
      possibleToday: streak.possible_today || 0,
      possibleTodayCore: streak.possible_today_core ?? streak.possible_today ?? 0,
      possibleTodayBonus: streak.possible_today_bonus ?? 0,
      weeklyBonusAmount: streak.weekly_bonus_amount || 100,
      weeklyBonusGranted: !!streak.weekly_bonus_granted,
      // iter_216 — truthful bonus outcome.
      weeklyBonusAmountGranted: streak.weekly_bonus_amount_granted || 0,
      weeklyBonusPartial: !!streak.weekly_bonus_partial,
      weeklyBonusCapBlocked: !!streak.weekly_bonus_cap_blocked,
    };
  }, [data]);

  useEffect(() => {
    if (loading || !data) return;
    if (summary.total > 0 && summary.completed === summary.total && !autoCollapsed) {
      setCollapsed(true);
      writeCollapsed(true);
      setAutoCollapsed(true);
    }
  }, [loading, data, summary, autoCollapsed]);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(next);
  };

  if (loading) return null;
  if (!data || !summary.total) return null;

  // Compact strip.
  if (collapsed) {
    return (
      <div
        className={`rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-900/15 via-purple-900/10 to-black px-3 py-2 ${className}`}
        data-testid="daily-tasks-card-collapsed"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 flex-wrap min-w-0">
            <span className="text-amber-300 font-black text-sm">🪙 Daily Tasks</span>
            <span
              className="text-orange-300 text-xs font-black"
              data-testid="daily-tasks-collapsed-day"
            >
              🔥 Day {summary.day}/7
            </span>
            <span
              className="text-white text-xs font-black"
              data-testid="daily-tasks-collapsed-progress"
            >
              {summary.completed}/{summary.total} complete
            </span>
            <span
              className={`text-xs font-black ${summary.capReached ? "text-rose-300" : "text-amber-200"}`}
              data-testid="daily-tasks-collapsed-earned"
            >
              🪙 {summary.earned}/{summary.cap} earned
            </span>
          </div>
          <button
            onClick={toggle}
            className="text-[11px] font-black text-amber-200 hover:text-amber-100 underline underline-offset-2 flex-shrink-0"
            data-testid="daily-tasks-expand-btn"
          >
            Expand →
          </button>
        </div>
      </div>
    );
  }

  const isDay7 = summary.day === 7;
  const day7AllDone = isDay7 && summary.completed === summary.total;

  // iter_222 — Render a single task row (used by both Core + Bonus).
  const renderTaskRow = (t, kind = "core") => {
    const pct = Math.min(100, Math.round((t.progress / Math.max(1, t.target)) * 100));
    const done = t.completed;
    const isBonus = kind === "bonus";
    return (
      <div
        key={t.task_id}
        className={`rounded-lg border px-3 py-2 ${
          done
            ? "border-emerald-500/40 bg-emerald-500/10"
            : isBonus
              ? "border-fuchsia-500/30 bg-fuchsia-500/5"
              : "border-white/10 bg-white/[0.03]"
        }`}
        data-testid={`daily-task-${t.task_id}`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-white font-black text-sm truncate">
              {done ? "✓ " : ""}
              {t.title}
              {t.scales && (
                <span className="ml-1.5 text-[9px] uppercase tracking-widest text-orange-300 font-black">
                  Day {t.day}
                </span>
              )}
              {isBonus && !done && (
                <span className="ml-1.5 text-[9px] uppercase tracking-widest text-fuchsia-300 font-black">
                  Bonus
                </span>
              )}
            </p>
            <p className="text-[11px] text-gray-500 truncate">{t.description}</p>
          </div>
          <div className="text-right flex-shrink-0 flex items-center gap-2">
            {done ? (
              <span
                className="text-xs font-black text-emerald-200"
                data-testid={`daily-task-status-${t.task_id}`}
              >
                {t.cap_reached
                  ? `Cap reached · 0 coins`
                  : `+🪙 ${t.amount_granted || t.amount}`}
              </span>
            ) : (
              <span
                className="text-xs font-black text-amber-200"
                data-testid={`daily-task-progress-${t.task_id}`}
              >
                {t.progress}/{t.target} · 🪙 {t.amount}
              </span>
            )}
            {isBonus && !done && t.cta_href && (
              <a
                href={t.cta_href}
                className="text-[10px] font-black text-fuchsia-200 hover:text-fuchsia-100 underline underline-offset-2"
                data-testid={`daily-task-cta-${t.task_id}`}
              >
                {t.cta_label || "Go"} →
              </a>
            )}
          </div>
        </div>
        <div className="mt-1.5 h-1 bg-black/40 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${done ? "bg-emerald-400" : isBonus ? "bg-fuchsia-400" : "bg-amber-400"}`}
            style={{ width: `${done ? 100 : pct}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className={`rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-900/15 via-purple-900/10 to-black p-4 sm:p-5 ${className}`}
      data-testid="daily-tasks-card"
    >
      {/* Header — Streak + day marker + earned meter */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-black">
            🪙 Daily Tasks · Weekly Rank-Up
          </p>
          <h3 className="text-white font-black text-lg flex items-center gap-2" data-testid="daily-tasks-header">
            <span data-testid="daily-tasks-day-badge" className="text-orange-300">🔥 Day {summary.day} of 7</span>
            <span className="text-gray-400 font-normal text-sm">·</span>
            <span className="text-white">{summary.completed}/{summary.total} today</span>
          </h3>
          <p className="text-[11px] text-amber-200/80 mt-0.5" data-testid="daily-tasks-streak-line">
            🔥 {summary.streakDays}-day streak · +🪙 {summary.possibleToday} available today · Resets at midnight PT
          </p>
        </div>
        <div className="flex items-start gap-3">
          <div className="text-right">
            <p className="text-[10px] uppercase text-gray-400">Earned today</p>
            <p
              className={`text-lg font-black ${summary.capReached ? "text-rose-300" : "text-amber-200"}`}
              data-testid="daily-tasks-earned"
            >
              🪙 {summary.earned}<span className="text-gray-500 text-xs"> / {summary.cap}</span>
            </p>
          </div>
          <button
            onClick={toggle}
            className="text-[11px] font-black text-amber-200 hover:text-amber-100 underline underline-offset-2 mt-1 flex-shrink-0"
            data-testid="daily-tasks-collapse-btn"
            title="Collapse Daily Tasks"
          >
            Hide
          </button>
        </div>
      </div>

      {/* Weekly progress dots */}
      <div
        className="flex items-center gap-1 mb-4"
        data-testid="daily-tasks-week-dots"
        aria-label={`Weekly Rank-Up: Day ${summary.day} of 7`}
      >
        {[1, 2, 3, 4, 5, 6, 7].map((d) => (
          <div
            key={d}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              d < summary.day
                ? "bg-emerald-400"
                : d === summary.day
                  ? "bg-orange-400"
                  : "bg-white/10"
            }`}
            data-testid={`daily-tasks-day-dot-${d}`}
          />
        ))}
      </div>

      {/* iter_222 — DAILY CORE section (streak-controlling) */}
      <div className="mb-2 flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest text-orange-300 font-black" data-testid="daily-tasks-core-heading">
          🔥 Daily Core
        </span>
        <span className="text-[10px] text-gray-500 font-black" data-testid="daily-tasks-core-progress">
          {summary.coreCompleted}/{summary.coreTotal} · +🪙 {summary.possibleTodayCore}
        </span>
      </div>
      <div className="space-y-2">
        {summary.coreTasks.map((t) => renderTaskRow(t, "core"))}
      </div>

      {/* iter_222 — TODAY'S ROTATING BONUS (optional, PT-day-stable) */}
      {summary.bonusTask && (
        <>
          <div className="mt-4 mb-2 flex items-center justify-between gap-2">
            <span
              className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black"
              data-testid="daily-tasks-bonus-heading"
            >
              ✨ Today&apos;s Bonus
            </span>
            <span
              className="text-[10px] text-gray-500 font-black"
              data-testid="daily-tasks-bonus-optional"
            >
              Optional · won&apos;t break your streak
            </span>
          </div>
          {renderTaskRow(summary.bonusTask, "bonus")}
        </>
      )}

      {/* Weekly Completion Bonus card — shown on Day 7. iter_216: honest
          about which of the three states applies — never claim coins
          were awarded when they weren't. */}
      {isDay7 && (() => {
        // A settled bonus is one that has actually run through the
        // grant path (or been explicitly blocked). Presence of
        // weeklyBonusGranted alone isn't enough — legacy docs may
        // carry it without the newer amount/partial/blocked fields.
        const settled =
          summary.weeklyBonusGranted &&
          (summary.weeklyBonusAmountGranted > 0 || summary.weeklyBonusCapBlocked);
        const fullyGranted =
          settled && !summary.weeklyBonusCapBlocked && !summary.weeklyBonusPartial;
        const partial =
          settled && summary.weeklyBonusPartial && summary.weeklyBonusAmountGranted > 0;
        const capBlocked = settled && summary.weeklyBonusCapBlocked;
        let tone = "border-orange-500/30 bg-orange-500/5";
        if (fullyGranted) tone = "border-emerald-500/40 bg-emerald-500/10";
        else if (partial) tone = "border-amber-500/40 bg-amber-500/10";
        else if (capBlocked) tone = "border-rose-500/40 bg-rose-500/10";
        else if (day7AllDone) tone = "border-orange-500/40 bg-orange-500/10";

        let statusLabel;
        let statusTestId = "daily-tasks-weekly-bonus-status-pending";
        let amountLabel;
        let amountTone = "text-orange-300";
        if (fullyGranted) {
          // iter_216 — render the actual granted amount so a legacy
          // streak doc can never render a false '+100 deposited'.
          statusLabel = `+🪙 ${summary.weeklyBonusAmountGranted} coins deposited to your wallet.`;
          statusTestId = "daily-tasks-weekly-bonus-status-granted";
          amountLabel = `+🪙 ${summary.weeklyBonusAmountGranted}`;
          amountTone = "text-emerald-300";
        } else if (partial) {
          statusLabel = `Only 🪙 ${summary.weeklyBonusAmountGranted} of ${summary.weeklyBonusAmount} coins fit — you hit the ${summary.cap}/day cap.`;
          statusTestId = "daily-tasks-weekly-bonus-status-partial";
          amountLabel = `+🪙 ${summary.weeklyBonusAmountGranted} of ${summary.weeklyBonusAmount}`;
          amountTone = "text-amber-300";
        } else if (capBlocked) {
          statusLabel = `The ${summary.cap}/day cap was already reached — 0 coins from the bonus landed today.`;
          statusTestId = "daily-tasks-weekly-bonus-status-blocked";
          amountLabel = `+🪙 0 of ${summary.weeklyBonusAmount}`;
          amountTone = "text-rose-300";
        } else if (day7AllDone) {
          statusLabel = "Complete! Your bonus lands as soon as the last task processes.";
          amountLabel = `+🪙 ${summary.weeklyBonusAmount}`;
        } else {
          statusLabel = "Complete all Day 7 tasks to unlock this bonus.";
          amountLabel = `+🪙 ${summary.weeklyBonusAmount}`;
        }

        return (
          <div
            className={`mt-4 p-3 rounded-lg border ${tone}`}
            data-testid="daily-tasks-weekly-bonus-card"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-orange-200 font-black text-sm">
                  🏆 Weekly Rank-Up Bonus
                </p>
                <p
                  className="text-[11px] text-gray-300 mt-0.5"
                  data-testid={statusTestId}
                >
                  {statusLabel}
                </p>
              </div>
              <p
                className={`font-black text-lg flex-shrink-0 ${amountTone}`}
                data-testid="daily-tasks-weekly-bonus-amount"
              >
                {amountLabel}
              </p>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default DailyTasksCard;
